import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FaArchive,
  FaArrowLeft,
  FaComments,
  FaFileAlt,
  FaImage,
  FaEllipsisH,
  FaPen,
  FaInbox,
  FaPaperclip,
  FaPaperPlane,
  FaRedoAlt,
  FaSearch,
  FaShoppingBag,
  FaTimes,
  FaTrash,
  FaUserFriends,
} from "react-icons/fa";
import { supabase } from "../supabaseClient";
import UserAvatar from "./UserAvatar";
import "./SocialMessages.css";

function PrivateMessageAttachment({
  message,
  attachment,
  getUrl,
}) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    // Don't request a private URL until the message
    // has been saved and has its real database UUID.
    if (
      !message?.id ||
      message.optimistic ||
      String(message.id).startsWith("temp-")
    ) {
      setLoading(true);

      return () => {
        active = false;
      };
    }

    const load = async () => {
      try {
        setLoading(true);

        const nextUrl = await getUrl(
          message,
          attachment
        );

        if (active) {
          setUrl(nextUrl);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [
    message.id,
    message.optimistic,
    attachment.object_key,
    getUrl,
  ]);

  const isImage =
    attachment.type?.startsWith("image");

  if (loading) {
    return (
      <div className="message-attachment">
        Loading attachment...
      </div>
    );
  }

  if (!url) {
    return (
      <div className="message-attachment">
        Attachment unavailable
      </div>
    );
  }

  return (
    <div className="message-attachment">
      {isImage ? (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="message-attachment__image"
        >
          <img
            src={url}
            alt={
              attachment.name ||
              "Message attachment"
            }
          />
        </a>
      ) : (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="message-attachment__file"
        >
          <FaFileAlt />
          <span>
            {attachment.name || "Open attachment"}
          </span>
        </a>
      )}
    </div>
  );
}

export default function SocialMessages({ currentUser, onBack, refreshUnread }) {
  const [friends, setFriends] = useState([]);
  const [selectedFriend, setSelectedFriend] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [file, setFile] = useState(null);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [conversationSearch, setConversationSearch] = useState("");
  const [conversationMeta, setConversationMeta] = useState({});
  const [attachmentUrls, setAttachmentUrls] = useState({});
  const [archivedAtByFriend, setArchivedAtByFriend] = useState({});
  const [showNewMessage, setShowNewMessage] = useState(false);
  const [newMessageSearch, setNewMessageSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [openMessageMenuId, setOpenMessageMenuId] = useState(null);
  const [uiNotice, setUiNotice] = useState(null);

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  const GUEST_UUID = "5ce4c73c-9222-477c-8308-4bff6e920370";

  const guestFriend = useMemo(
    () => ({
      friend_id: "guest_offers",
      friend_email: "Guest / Offers",
      friend_display_name: "Guest / Offers",
      created_at: null,
      isGuestOffers: true,
    }),
    []
  );

  const getPrivateAttachmentUrl = useCallback(
    async (message, attachment) => {
      if (!message?.id || !attachment?.object_key) {
        return null;
      }

      const cacheKey =
        `${message.id}:${attachment.object_key}`;

      if (attachmentUrls[cacheKey]) {
        return attachmentUrls[cacheKey];
      }

      const { data, error } =
        await supabase.functions.invoke(
          "get-private-message-attachment",
          {
            body: {
              messageId: message.id,
              objectKey: attachment.object_key,
            },
          }
        );

      if (error) {
        console.error(
          "Unable to authorize attachment:",
          error
        );
        return null;
      }

      if (!data?.success || !data?.url) {
        console.error(
          "Invalid private attachment response:",
          data
        );
        return null;
      }

      setAttachmentUrls((prev) => ({
        ...prev,
        [cacheKey]: data.url,
      }));

      return data.url;
    },
    [attachmentUrls]
  );

  const formatDate = (value) => {
    if (!value) return "";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";

    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatMessageTime = (value) => {
    if (!value) return "";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";

    return date.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const formatMessageDay = (value) => {
    if (!value) return "";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";

    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    const sameDay = (a, b) =>
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate();

    if (sameDay(date, today)) return "Today";
    if (sameDay(date, yesterday)) return "Yesterday";

    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year:
        date.getFullYear() !== today.getFullYear()
          ? "numeric"
          : undefined,
    });
  };

  const getInitials = (name) => {
    if (!name) return "?";

    const parts = String(name)
      .trim()
      .split(/\s+/)
      .filter(Boolean);

    if (!parts.length) return "?";
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();

    return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`
      .toUpperCase();
  };

  const getFriendName = (friend) =>
    friend?.friend_display_name ||
    friend?.friend_email ||
    "Hobbli Collector";

  const getAttachments = (attachments) => {
    if (!attachments) return [];

    if (Array.isArray(attachments)) return attachments;

    if (typeof attachments === "string") {
      try {
        const parsed = JSON.parse(attachments);
        return Array.isArray(parsed) ? parsed : [];
      } catch (err) {
        console.warn("Unable to parse message attachments:", err);
        return [];
      }
    }

    return [];
  };

  const parseMarketplaceInquiry = (content) => {
    if (!content || !/buyer interested in your item/i.test(content)) {
      return null;
    }

    const capture = (label, nextLabels) => {
      const lookahead = nextLabels
        .map((next) => `\\s+${next}:`)
        .join("|");

      const regex = new RegExp(
        `${label}:\\s*(.*?)(?=${lookahead}|$)`,
        "i"
      );

      return content.match(regex)?.[1]?.trim() || "";
    };

    const item = capture("ITEM", [
      "Price",
      "BUYER",
      "Email",
      "Location",
      "MESSAGE",
    ]);
    const price = capture("Price", [
      "BUYER",
      "Email",
      "Location",
      "MESSAGE",
    ]);
    const buyer = capture("BUYER", [
      "Email",
      "Location",
      "MESSAGE",
    ]);
    const email = capture("Email", ["Location", "MESSAGE"]);
    const location = capture("Location", ["MESSAGE"]);
    const message = capture("MESSAGE", []);

    if (!item && !buyer && !message) return null;

    return {
      item,
      price,
      buyer,
      email,
      location,
      message,
    };
  };

  const fetchConversationMeta = async (friendList = friends) => {
    if (!currentUser?.id) return;

    try {
      const { data, error } = await supabase
        .from("messages")
        .select(
          "id, sender_id, receiver_id, content, created_at, read_at"
        )
        .or(
          `sender_id.eq.${currentUser.id},receiver_id.eq.${currentUser.id}`
        )
        .order("created_at", { ascending: false })
        .limit(300);

      if (error) throw error;

      const friendIds = new Set(
        (friendList || []).map((friend) => friend.friend_id)
      );

      const nextMeta = {};

      (data || []).forEach((message) => {
        const otherPartyId =
          message.sender_id === currentUser.id
            ? message.receiver_id
            : message.sender_id;

        const key = friendIds.has(otherPartyId)
          ? otherPartyId
          : "guest_offers";

        if (!nextMeta[key]) {
          nextMeta[key] = {
            lastMessage: message.content || "Attachment",
            lastMessageAt: message.created_at,
            unreadCount: 0,
          };
        }

        if (
          message.receiver_id === currentUser.id &&
          !message.read_at
        ) {
          nextMeta[key].unreadCount += 1;
        }
      });

      setConversationMeta(nextMeta);
    } catch (err) {
      console.error("fetchConversationMeta error:", err);
    }
  };

  const fetchConversationPreferences = async () => {
    if (!currentUser?.id) return;

    const { data, error } = await supabase
      .from("message_conversation_preferences")
      .select("other_user_id, archived_at")
      .eq("user_id", currentUser.id)
      .not("archived_at", "is", null);

    if (error) {
      console.error("fetchConversationPreferences error:", error);
      return;
    }

    const next = {};
    (data || []).forEach((row) => {
      if (row.other_user_id && row.archived_at) {
        next[row.other_user_id] = row.archived_at;
      }
    });
    setArchivedAtByFriend(next);
  };

  const fetchFriends = async () => {
    if (!currentUser?.id) return;

    try {
      const { data, error } = await supabase.rpc(
        "get_user_connections"
      );

      if (error) throw error;

      const nextFriends = data || [];
      setFriends(nextFriends);
      await Promise.all([
        fetchConversationMeta(nextFriends),
        fetchConversationPreferences(),
      ]);
    } catch (err) {
      console.error("fetchFriends error:", err);
    }
  };

  const markConversationAsRead = async (
    friend,
    conversationMessages = []
  ) => {
    if (!currentUser?.id || !friend) return;

    try {
      if (friend.friend_id === "guest_offers") {
        const unreadIds = conversationMessages
          .filter(
            (message) =>
              message.receiver_id === currentUser.id &&
              !message.read_at
          )
          .map((message) => message.id)
          .filter(Boolean);

        if (unreadIds.length > 0) {
          const { error } = await supabase
            .from("messages")
            .update({ read_at: new Date().toISOString() })
            .in("id", unreadIds);

          if (error) throw error;
        }
      } else {
        const { error } = await supabase
          .from("messages")
          .update({ read_at: new Date().toISOString() })
          .eq("receiver_id", currentUser.id)
          .eq("sender_id", friend.friend_id)
          .is("read_at", null);

        if (error) throw error;
      }

      setConversationMeta((prev) => ({
        ...prev,
        [friend.friend_id]: {
          ...(prev[friend.friend_id] || {}),
          unreadCount: 0,
        },
      }));

      refreshUnread?.();
    } catch (err) {
      console.error("markConversationAsRead error:", err);
    }
  };

  const fetchMessagesForSelectedFriend = async (friend) => {
    if (!currentUser?.id || !friend) return;

    setLoadingMessages(true);

    try {
      let combined = [];

      if (friend.friend_id === "guest_offers") {
        const { data, error } = await supabase
          .from("messages")
          .select("*")
          .or(
            `sender_id.eq.${currentUser.id},receiver_id.eq.${currentUser.id}`
          )
          .order("created_at", { ascending: true })
          .limit(300);

        if (error) throw error;

        const friendIds = new Set(
          friends.map((item) => item.friend_id)
        );

        combined = (data || []).filter((message) => {
          const otherPartyId =
            message.sender_id === currentUser.id
              ? message.receiver_id
              : message.sender_id;

          return (
            otherPartyId === GUEST_UUID ||
            !friendIds.has(otherPartyId)
          );
        });
      } else {
        const { data, error } = await supabase.rpc(
          "get_direct_messages",
          {
            p_with_user: friend.friend_id,
            p_limit: 200,
            p_offset: 0,
          }
        );

        if (error) throw error;

        combined = data || [];
      }

      combined.sort(
        (a, b) =>
          new Date(a.created_at) - new Date(b.created_at)
      );

      setMessages(combined);
      await markConversationAsRead(friend, combined);
    } catch (err) {
      console.error(
        "fetchMessagesForSelectedFriend error:",
        err
      );
      setMessages([]);
    } finally {
      setLoadingMessages(false);

      window.setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({
          behavior: "auto",
        });
      }, 80);
    }
  };

  const handleDeleteMessage = async (message) => {
    if (!message?.id || message.optimistic) return;
    if (message.sender_id !== currentUser?.id) return;

    const confirmed = window.confirm(
      "Delete this message for everyone? This cannot be undone."
    );

    if (!confirmed) return;

    const { data, error } = await supabase.functions.invoke(
      "delete-r2-media",
      {
        body: {
          resourceType: "message",
          resourceId: message.id,
        },
      }
    );

    if (error || data?.success !== true) {
      console.error(
        "DELETE MESSAGE ERROR:",
        error || data?.error || data
      );
      setUiNotice({ type: "error", message: "Unable to delete message." });
      return;
    }

    setMessages((prev) =>
      prev.filter((item) => item.id !== message.id)
    );

    setAttachmentUrls((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (key.startsWith(`${message.id}:`)) {
          delete next[key];
        }
      });
      return next;
    });

    await fetchConversationMeta(friends);
    refreshUnread?.();
  };

  const handleArchiveConversation = async () => {
    if (!currentUser?.id || !selectedFriend) return;

    if (selectedFriend.isGuestOffers) {
      setUiNotice({ type: "error", message: "Guest / Offers cannot be archived as one conversation." });
      return;
    }

    const archivedAt = new Date().toISOString();
    const { error } = await supabase
      .from("message_conversation_preferences")
      .upsert(
        {
          user_id: currentUser.id,
          other_user_id: selectedFriend.friend_id,
          archived_at: archivedAt,
        },
        { onConflict: "user_id,other_user_id" }
      );

    if (error) {
      console.error("ARCHIVE CONVERSATION ERROR:", error);
      setUiNotice({ type: "error", message: "Unable to archive conversation." });
      return;
    }

    setArchivedAtByFriend((prev) => ({
      ...prev,
      [selectedFriend.friend_id]: archivedAt,
    }));
    setSelectedFriend(null);
    setMessages([]);
    setAttachmentUrls({});
  };

  const handleSendMessage = async () => {
    const trimmedMessage = newMessage.trim();

    if (!currentUser?.id || !selectedFriend) return;
    if (!trimmedMessage && !file) return;
    if (sending) return;

    setSending(true);

    let uploadedAttachment = null;

    try {
      let attachments = [];

      if (file) {
        const { data: uploadAuth, error: uploadAuthError } =
          await supabase.functions.invoke(
            "create-private-message-upload",
            {
              body: {
                contentType: file.type,
                fileSize: file.size,
              },
            }
          );

        if (uploadAuthError) {
          throw uploadAuthError;
        }

        if (
          !uploadAuth?.success ||
          !uploadAuth?.uploadUrl ||
          !uploadAuth?.storage?.objectKey
        ) {
          throw new Error(
            uploadAuth?.error ||
              "Unable to authorize attachment upload"
          );
        }

        const uploadResponse = await fetch(
          uploadAuth.uploadUrl,
          {
            method: "PUT",
            headers: {
              "Content-Type": file.type,
            },
            body: file,
          }
        );

        if (!uploadResponse.ok) {
          throw new Error(
            `Private attachment upload failed (${uploadResponse.status})`
          );
        }

        attachments = [
          {
            storage_provider: "r2",
            storage_bucket: uploadAuth.storage.bucket,
            object_key: uploadAuth.storage.objectKey,
            name: file.name,
            type: file.type || "file",
            file_size_bytes: file.size,
          },
        ];

        uploadedAttachment = attachments[0];
      }

      const recipientId =
        selectedFriend.friend_id === "guest_offers"
          ? GUEST_UUID
          : selectedFriend.friend_id;

      const optimisticMessage = {
        id: `temp-${Date.now()}`,
        sender_id: currentUser.id,
        receiver_id: recipientId,
        content: trimmedMessage,
        attachments: attachments.length
          ? attachments
          : null,
        created_at: new Date().toISOString(),
        read_at: null,
        optimistic: true,
      };

      setMessages((prev) => [
        ...prev,
        optimisticMessage,
      ]);

      setNewMessage("");
      setFile(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }

      const { data, error } = await supabase.rpc(
        "send_direct_message",
        {
          sender: currentUser.id,
          recipient: recipientId,
          content: trimmedMessage,
          _attachments: attachments.length
            ? attachments
            : null,
        }
      );

      if (error) throw error;

      const messageId = data || null;

      setMessages((prev) =>
        prev.map((message) =>
          message.id === optimisticMessage.id
            ? {
                ...message,
                id: messageId || message.id,
                optimistic: false,
              }
            : message
        )
      );

      await fetchConversationMeta(friends);
    } catch (err) {
      console.error("handleSendMessage error:", err);

      if (uploadedAttachment?.object_key && currentUser?.id) {
        const { data: cleanupData, error: cleanupError } =
          await supabase.functions.invoke(
            "delete-r2-media",
            {
              body: {
                resourceType: "message_attachment",
                resourceId: currentUser.id,
                objectKey: uploadedAttachment.object_key,
              },
            }
          );

        if (cleanupError || cleanupData?.success !== true) {
          console.error(
            "FAILED MESSAGE ATTACHMENT CLEANUP ERROR:",
            cleanupError || cleanupData?.error || cleanupData
          );
        }
      }

      setUiNotice({ type: "error", message: "Failed to send message. Please try again." });

      setMessages((prev) =>
        prev.filter((message) => !message.optimistic)
      );
    } finally {
      setSending(false);

      window.setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({
          behavior: "smooth",
        });
      }, 80);
    }
  };

  const handleFileChange = (event) => {
    setFile(event.target.files?.[0] || null);
  };

  const handleComposerChange = (event) => {
    setNewMessage(event.target.value);

    const target = event.target;
    target.style.height = "auto";
    target.style.height = `${Math.min(
      target.scrollHeight,
      140
    )}px`;
  };

  const handleComposerKeyDown = (event) => {
    if (
      event.key === "Enter" &&
      !event.shiftKey &&
      !event.nativeEvent?.isComposing
    ) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const handleUnarchiveConversation = async (friend, { open = false } = {}) => {
    if (!currentUser?.id || !friend?.friend_id || friend.isGuestOffers) return;

    const { error } = await supabase
      .from("message_conversation_preferences")
      .upsert(
        {
          user_id: currentUser.id,
          other_user_id: friend.friend_id,
          archived_at: null,
        },
        { onConflict: "user_id,other_user_id" }
      );

    if (error) {
      console.error("UNARCHIVE CONVERSATION ERROR:", error);
      setUiNotice({ type: "error", message: "Unable to restore conversation." });
      return;
    }

    setArchivedAtByFriend((prev) => {
      const next = { ...prev };
      delete next[friend.friend_id];
      return next;
    });

    if (open) {
      setShowArchived(false);
      setSelectedFriend(friend);
      setMessages([]);
    }
  };

  const handleStartConversation = async (friend) => {
    if (!friend) return;

    if (archivedAtByFriend[friend.friend_id]) {
      await handleUnarchiveConversation(friend);
    }

    setShowNewMessage(false);
    setNewMessageSearch("");
    setShowArchived(false);
    setSelectedFriend(friend);
    setMessages([]);
  };

  const handleSelectFriend = (friend) => {
    setOpenMessageMenuId(null);
    setSelectedFriend(friend);
    setMessages([]);
  };

  const handleConversationBack = () => {
    setSelectedFriend(null);
    setMessages([]);
  };

  const inboxFriends = useMemo(() => {
    const visibleFriends = friends.filter((friend) => {
      const meta = conversationMeta[friend.friend_id];
      if (!meta?.lastMessageAt) return false;

      const archivedAt = archivedAtByFriend[friend.friend_id];
      if (!archivedAt) return true;

      // A new message after the archive point automatically
      // brings the conversation back into the inbox.
      return new Date(meta.lastMessageAt) > new Date(archivedAt);
    });

    visibleFriends.sort((a, b) =>
      new Date(conversationMeta[b.friend_id]?.lastMessageAt || 0) -
      new Date(conversationMeta[a.friend_id]?.lastMessageAt || 0)
    );

    return [guestFriend, ...visibleFriends];
  }, [friends, conversationMeta, guestFriend, archivedAtByFriend]);

  const archivedFriends = useMemo(() => {
    return friends
      .filter((friend) => {
        const archivedAt = archivedAtByFriend[friend.friend_id];
        if (!archivedAt) return false;

        const lastMessageAt = conversationMeta[friend.friend_id]?.lastMessageAt;
        return !lastMessageAt || new Date(lastMessageAt) <= new Date(archivedAt);
      })
      .sort((a, b) =>
        new Date(archivedAtByFriend[b.friend_id] || 0) -
        new Date(archivedAtByFriend[a.friend_id] || 0)
      );
  }, [friends, conversationMeta, archivedAtByFriend]);

  const displayedFriends = showArchived ? archivedFriends : inboxFriends;

  const filteredFriends = useMemo(() => {
    const query = conversationSearch.trim().toLowerCase();
    if (!query) return displayedFriends;

    return displayedFriends.filter((friend) => {
      const name = getFriendName(friend).toLowerCase();
      const email = String(friend.friend_email || "").toLowerCase();
      return name.includes(query) || email.includes(query);
    });
  }, [displayedFriends, conversationSearch]);

  const composeFriends = useMemo(() => {
    const query = newMessageSearch.trim().toLowerCase();
    return [...friends]
      .sort((a, b) => getFriendName(a).localeCompare(getFriendName(b)))
      .filter((friend) => {
        if (!query) return true;
        const name = getFriendName(friend).toLowerCase();
        const email = String(friend.friend_email || "").toLowerCase();
        return name.includes(query) || email.includes(query);
      });
  }, [friends, newMessageSearch]);

  useEffect(() => {
    if (!currentUser?.id) return;

    fetchFriends();
  }, [currentUser?.id]);

  useEffect(() => {
    if (!selectedFriend) {
      setMessages([]);
      return;
    }

    fetchMessagesForSelectedFriend(selectedFriend);
  }, [selectedFriend?.friend_id, currentUser?.id, friends]);

  useEffect(() => {
    if (!currentUser?.id) return;

    const receiverChannel = supabase
      .channel(`messages_receiver_${currentUser.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "messages",
          filter: `receiver_id=eq.${currentUser.id}`,
        },
        async (payload) => {
          const message = payload.new;

          const relevantToSelected =
            selectedFriend &&
            (selectedFriend.friend_id === "guest_offers"
              ? message.sender_id === GUEST_UUID ||
                !friends.some(
                  (friend) =>
                    friend.friend_id ===
                    message.sender_id
                )
              : message.sender_id ===
                selectedFriend.friend_id);

          if (relevantToSelected) {
            setMessages((prev) => {
              if (
                prev.some(
                  (existing) =>
                    existing.id === message.id
                )
              ) {
                return prev;
              }

              return [...prev, message];
            });

            await markConversationAsRead(
              selectedFriend,
              [message]
            );

            window.setTimeout(() => {
              messagesEndRef.current?.scrollIntoView({
                behavior: "smooth",
              });
            }, 60);
          }

          await fetchConversationMeta(friends);
          refreshUnread?.();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(receiverChannel);
    };
  }, [
    currentUser?.id,
    selectedFriend?.friend_id,
    friends,
  ]);

  const selectedName = getFriendName(selectedFriend);
  const selectedMeta =
    conversationMeta[selectedFriend?.friend_id] || {};

  return (
    <div
      className={`hobbli-messages-page ${
        selectedFriend ? "is-chat-open" : ""
      }`}
    >
      <div style={{ position: "absolute", left: "50%", transform: "translateX(-50%)", zIndex: 20, width: "min(520px, calc(100% - 24px))", top: "12px" }}>
        {uiNotice ? (
        <div role="status" style={{ margin: "0 0 14px", padding: "10px 12px", borderRadius: "8px", background: uiNotice.type === "error" ? "rgba(220, 53, 69, 0.10)" : "rgba(47, 128, 237, 0.10)", color: uiNotice.type === "error" ? "#b42318" : "inherit", fontSize: ".88rem" }}>
          {uiNotice.message}
        </div>
      ) : null}
      </div>
      <aside className="messages-sidebar">
        <div className="messages-sidebar__top">
          <div className="messages-sidebar__title-row">
            <div>
              <span className="messages-eyebrow">
                Hobbli Social
              </span>
              <h2>Messages</h2>
            </div>

            <div className="messages-sidebar__actions">
              <button
                type="button"
                className="messages-new-button"
                onClick={() => setShowNewMessage(true)}
              >
                <FaPen />
                <span>New</span>
              </button>

              <button
                type="button"
                className="messages-icon-button"
                onClick={fetchFriends}
                title="Refresh conversations"
                aria-label="Refresh conversations"
              >
                <FaRedoAlt />
              </button>
            </div>
          </div>

          <p>
            Talk collecting, share finds, and keep in
            touch with your network.
          </p>

          <div className="messages-view-tabs">
            <button
              type="button"
              className={!showArchived ? "is-active" : ""}
              onClick={() => setShowArchived(false)}
            >
              Inbox
            </button>
            <button
              type="button"
              className={showArchived ? "is-active" : ""}
              onClick={() => setShowArchived(true)}
            >
              Archived
              {archivedFriends.length > 0 && <span>{archivedFriends.length}</span>}
            </button>
          </div>

          <div className="messages-search">
            <FaSearch />
            <input
              type="text"
              value={conversationSearch}
              onChange={(event) =>
                setConversationSearch(
                  event.target.value
                )
              }
              placeholder={showArchived ? "Search archived..." : "Search conversations..."}
            />

            {conversationSearch && (
              <button
                type="button"
                onClick={() =>
                  setConversationSearch("")
                }
                aria-label="Clear conversation search"
              >
                ×
              </button>
            )}
          </div>
        </div>

        <div className="messages-sidebar__scroll">
          {filteredFriends.length > 0 ? (
            filteredFriends.map((friend) => {
              const active =
                selectedFriend?.friend_id ===
                friend.friend_id;
              const meta =
                conversationMeta[friend.friend_id] ||
                {};
              const unreadCount =
                meta.unreadCount || 0;
              const name = getFriendName(friend);

              return (
                <button
                  type="button"
                  key={friend.friend_id}
                  className={`conversation-row ${
                    active ? "is-active" : ""
                  }`}
                  onClick={() =>
                    showArchived
                      ? handleUnarchiveConversation(friend, { open: true })
                      : handleSelectFriend(friend)
                  }
                >
                  {friend.isGuestOffers ? (
                    <div className="conversation-avatar conversation-avatar--offers">
                      <FaShoppingBag />
                    </div>
                  ) : (
                    <UserAvatar
                      className="conversation-avatar"
                      src={friend.friend_profile_picture}
                      name={name}
                    />
                  )}

                  <div className="conversation-row__body">
                    <div className="conversation-row__topline">
                      <strong>{name}</strong>

                      {meta.lastMessageAt && (
                        <span>
                          {formatMessageTime(
                            meta.lastMessageAt
                          )}
                        </span>
                      )}
                    </div>

                    <div className="conversation-row__bottomline">
                      <span>
                        {meta.lastMessage
                          ? meta.lastMessage
                          : friend.isGuestOffers
                          ? "Marketplace and guest inquiries"
                          : "Start a conversation"}
                      </span>

                      {unreadCount > 0 && (
                        <b className="conversation-unread">
                          {unreadCount > 99
                            ? "99+"
                            : unreadCount}
                        </b>
                      )}
                    </div>
                  </div>
                </button>
              );
            })
          ) : (
            <div className="messages-sidebar-empty">
              <FaSearch />
              <strong>{showArchived ? "No archived conversations" : "No conversations found"}</strong>
              <span>
                {showArchived
                  ? "Archived conversations will appear here."
                  : "Start a new message with one of your friends."}
              </span>
            </div>
          )}
        </div>

        <div className="messages-sidebar__footer">
          <button
            type="button"
            className="messages-back-to-social"
            onClick={() => onBack?.()}
          >
            <FaArrowLeft />
            Back to Social
          </button>
        </div>
      </aside>

      <main className="messages-chat">
        {!currentUser?.id ? (
          <div className="messages-empty-chat">
            <div className="messages-empty-chat__icon">
              <FaComments />
            </div>
            <h3>Loading messages...</h3>
          </div>
        ) : selectedFriend ? (
          <>
            <header className="messages-chat-header">
              <button
                type="button"
                className="messages-mobile-back"
                onClick={handleConversationBack}
                aria-label="Back to conversations"
              >
                <FaArrowLeft />
              </button>

              {selectedFriend.isGuestOffers ? (
                <div className="conversation-avatar conversation-avatar--header conversation-avatar--offers">
                  <FaShoppingBag />
                </div>
              ) : (
                <UserAvatar
                  className="conversation-avatar conversation-avatar--header"
                  src={selectedFriend.friend_profile_picture}
                  name={selectedName}
                />
              )}

              <div className="messages-chat-header__identity">
                <strong>{selectedName}</strong>

                <span>
                  {selectedFriend.isGuestOffers
                    ? "Marketplace & guest inquiries"
                    : selectedFriend.created_at
                    ? `Friends since ${formatDate(
                        selectedFriend.created_at
                      )}`
                    : selectedFriend.friend_email}
                </span>
              </div>

              <div className="messages-chat-header__details">
                {!selectedFriend.isGuestOffers &&
                  selectedFriend.friend_email &&
                  selectedFriend.friend_email !== selectedName && (
                    <span className="messages-chat-header__email">
                      {selectedFriend.friend_email}
                    </span>
                  )}

                <span className="messages-chat-header__stat">
                  {messages.length} {messages.length === 1 ? "message" : "messages"}
                </span>

                {!selectedFriend.isGuestOffers && messages.length > 0 && (
                  <button
                    type="button"
                    className="messages-archive-button"
                    onClick={handleArchiveConversation}
                  >
                    <FaArchive />
                    <span>Archive conversation</span>
                  </button>
                )}
              </div>
            </header>

            <div className="messages-chat-scroll">
              {loadingMessages ? (
                <div className="messages-loading">
                  <span className="messages-spinner" />
                  <strong>Loading conversation</strong>
                  <span>
                    Pulling your latest messages...
                  </span>
                </div>
              ) : messages.length === 0 ? (
                <div className="messages-empty-chat">
                  <div className="messages-empty-chat__icon">
                    {selectedFriend.isGuestOffers ? (
                      <FaShoppingBag />
                    ) : (
                      <FaComments />
                    )}
                  </div>

                  <h3>
                    {selectedFriend.isGuestOffers
                      ? "No marketplace inquiries yet"
                      : `Start a conversation with ${selectedName}`}
                  </h3>

                  <p>
                    {selectedFriend.isGuestOffers
                      ? "Guest buyers and marketplace inquiries will appear here."
                      : "Send a message, share a photo, or talk about what you're collecting."}
                  </p>
                </div>
              ) : (
                messages.map((message, index) => {
                  const mine =
                    message.sender_id ===
                    currentUser.id;
                  const previousMessage =
                    messages[index - 1];

                  const showDay =
                    !previousMessage ||
                    formatMessageDay(
                      previousMessage.created_at
                    ) !==
                      formatMessageDay(
                        message.created_at
                      );

                  const grouped =
                    previousMessage &&
                    previousMessage.sender_id ===
                      message.sender_id &&
                    new Date(message.created_at) -
                      new Date(
                        previousMessage.created_at
                      ) <
                      5 * 60 * 1000 &&
                    !showDay;

                  const senderFriend = friends.find(
                    (friend) =>
                      friend.friend_id ===
                      message.sender_id
                  );

                  const senderLabel = mine
                    ? "You"
                    : message.sender_id === GUEST_UUID
                    ? "Guest Buyer"
                    : senderFriend?.friend_display_name ||
                      "Marketplace Guest";

                  const attachments =
                    getAttachments(
                      message.attachments
                    );

                  const inquiry =
                    parseMarketplaceInquiry(
                      message.content
                    );

                  return (
                    <React.Fragment key={message.id}>
                      {showDay && (
                        <div className="message-day-divider">
                          <span>
                            {formatMessageDay(
                              message.created_at
                            )}
                          </span>
                        </div>
                      )}

                      <div
                        className={`message-row ${
                          mine
                            ? "message-row--mine"
                            : "message-row--theirs"
                        } ${
                          grouped
                            ? "is-grouped"
                            : ""
                        }`}
                      >
                        {!mine && !grouped && (
                          <div className="message-row__sender">
                            {senderLabel}
                          </div>
                        )}

                        <div
                          className={`message-bubble ${
                            mine
                              ? "message-bubble--mine"
                              : "message-bubble--theirs"
                          } ${
                            inquiry
                              ? "message-bubble--inquiry"
                              : ""
                          }`}
                        >
                          {attachments.length > 0 && (
                            <div className="message-attachments">
                              {attachments.map(
                                (attachment, attachmentIndex) => (
                                  <PrivateMessageAttachment
                                    key={`${message.id}-attachment-${attachmentIndex}`}
                                    message={message}
                                    attachment={attachment}
                                    getUrl={getPrivateAttachmentUrl}
                                  />
                                )
                              )}
                            </div>
                          )}

                          {inquiry ? (
                            <div className="marketplace-inquiry">
                              <div className="marketplace-inquiry__header">
                                <span>
                                  <FaShoppingBag />
                                </span>

                                <div>
                                  <small>
                                    Marketplace Inquiry
                                  </small>
                                  <strong>
                                    {inquiry.item ||
                                      "Item inquiry"}
                                  </strong>
                                </div>
                              </div>

                              {(inquiry.price ||
                                inquiry.buyer ||
                                inquiry.email ||
                                inquiry.location) && (
                                <div className="marketplace-inquiry__details">
                                  {inquiry.price && (
                                    <div>
                                      <span>
                                        Price
                                      </span>
                                      <strong>
                                        {inquiry.price}
                                      </strong>
                                    </div>
                                  )}

                                  {inquiry.buyer && (
                                    <div>
                                      <span>
                                        Buyer
                                      </span>
                                      <strong>
                                        {inquiry.buyer}
                                      </strong>
                                    </div>
                                  )}

                                  {inquiry.email && (
                                    <div>
                                      <span>
                                        Email
                                      </span>
                                      <strong>
                                        {inquiry.email}
                                      </strong>
                                    </div>
                                  )}

                                  {inquiry.location && (
                                    <div>
                                      <span>
                                        Location
                                      </span>
                                      <strong>
                                        {inquiry.location}
                                      </strong>
                                    </div>
                                  )}
                                </div>
                              )}

                              {inquiry.message && (
                                <div className="marketplace-inquiry__message">
                                  <span>
                                    Message
                                  </span>
                                  <p>
                                    {inquiry.message}
                                  </p>
                                </div>
                              )}
                            </div>
                          ) : (
                            message.content && (
                              <div className="message-content">
                                {message.content}
                              </div>
                            )
                          )}

                          <div className="message-bubble__meta">
                            <span>
                              {formatMessageTime(
                                message.created_at
                              )}
                            </span>

                            {message.optimistic && (
                              <span>Sending...</span>
                            )}

                            {mine && !message.optimistic && (
                              <div className="message-actions">
                                <button
                                  type="button"
                                  className="message-actions__trigger"
                                  onClick={() =>
                                    setOpenMessageMenuId((current) =>
                                      current === message.id ? null : message.id
                                    )
                                  }
                                  title="Message options"
                                  aria-label="Message options"
                                  aria-expanded={openMessageMenuId === message.id}
                                >
                                  <FaEllipsisH />
                                </button>

                                {openMessageMenuId === message.id && (
                                  <div className="message-actions__menu">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setOpenMessageMenuId(null);
                                        handleDeleteMessage(message);
                                      }}
                                    >
                                      <FaTrash />
                                      Delete message
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })
              )}

              <div ref={messagesEndRef} />
            </div>

            <div className="messages-composer-shell">
              {file && (
                <div className="messages-file-preview">
                  <div className="messages-file-preview__icon">
                    {file.type?.startsWith("image") ? (
                      <FaImage />
                    ) : (
                      <FaFileAlt />
                    )}
                  </div>

                  <div className="messages-file-preview__name">
                    <strong>{file.name}</strong>
                    <span>
                      {Math.max(
                        1,
                        Math.round(file.size / 1024)
                      )}{" "}
                      KB
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setFile(null);

                      if (fileInputRef.current) {
                        fileInputRef.current.value =
                          "";
                      }
                    }}
                    aria-label="Remove attachment"
                  >
                    <FaTimes />
                  </button>
                </div>
              )}

              <div className="messages-composer">
                <input
                  ref={fileInputRef}
                  className="messages-hidden-file-input"
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={handleFileChange}
                />

                <textarea
                  ref={textareaRef}
                  className="messages-textarea"
                  rows={1}
                  placeholder={`Message ${selectedName}...`}
                  value={newMessage}
                  onChange={handleComposerChange}
                  onKeyDown={handleComposerKeyDown}
                />

                <button
                  type="button"
                  className="messages-attach-button"
                  onClick={() =>
                    fileInputRef.current?.click()
                  }
                  title="Attach image or PDF"
                  aria-label="Attach image or PDF"
                >
                  <FaPaperclip />
                </button>

                <button
                  type="button"
                  className="messages-send-button"
                  onClick={handleSendMessage}
                  disabled={
                    sending ||
                    (!newMessage.trim() && !file)
                  }
                >
                  <FaPaperPlane />
                  <span>
                    {sending ? "Sending" : "Send"}
                  </span>
                </button>
              </div>

              <div className="messages-composer-hint">
                <span>
                  Enter to send · Shift + Enter for a new
                  line
                </span>

                {selectedMeta.unreadCount > 0 && (
                  <span>
                    {selectedMeta.unreadCount} unread
                  </span>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="messages-empty-chat messages-empty-chat--landing">
            <div className="messages-empty-chat__icon messages-empty-chat__icon--large">
              <FaUserFriends />
            </div>

            <span className="messages-eyebrow">
              Hobbli Messages
            </span>

            <h2>Your collector conversations, together.</h2>

            <p>
              Choose a friend to chat, or open Guest /
              Offers to review marketplace inquiries.
            </p>

            <div className="messages-empty-chat__features">
              <span>
                <FaComments />
                Private conversations
              </span>

              <span>
                <FaImage />
                Photos & files
              </span>

              <span>
                <FaInbox />
                Marketplace inquiries
              </span>
            </div>
          </div>
        )}
      </main>

      {showNewMessage && (
        <div
          className="messages-modal-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setShowNewMessage(false);
          }}
        >
          <div className="messages-new-modal" role="dialog" aria-modal="true" aria-label="New message">
            <div className="messages-new-modal__header">
              <div>
                <span className="messages-eyebrow">Hobbli Social</span>
                <h3>New Message</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowNewMessage(false)}
                aria-label="Close new message"
              >
                <FaTimes />
              </button>
            </div>

            <div className="messages-new-modal__search">
              <FaSearch />
              <input
                autoFocus
                type="text"
                value={newMessageSearch}
                onChange={(event) => setNewMessageSearch(event.target.value)}
                placeholder="Search friends..."
              />
            </div>

            <div className="messages-new-modal__list">
              {composeFriends.length > 0 ? (
                composeFriends.map((friend) => {
                  const name = getFriendName(friend);
                  const hasConversation = Boolean(conversationMeta[friend.friend_id]?.lastMessageAt);
                  const isArchived = Boolean(archivedAtByFriend[friend.friend_id]);
                  return (
                    <button
                      type="button"
                      key={friend.friend_id}
                      className="messages-new-friend"
                      onClick={() => handleStartConversation(friend)}
                    >
                      <UserAvatar
                        className="conversation-avatar"
                        src={friend.friend_profile_picture}
                        name={name}
                      />
                      <span className="messages-new-friend__identity">
                        <strong>{name}</strong>
                        <small>{friend.friend_email || (hasConversation ? "Open conversation" : "Start a conversation")}</small>
                      </span>
                      <span className="messages-new-friend__status">
                        {isArchived ? "Restore" : hasConversation ? "Open" : "Message"}
                      </span>
                    </button>
                  );
                })
              ) : (
                <div className="messages-new-modal__empty">No friends match that search.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
